"use client";

import * as React from "react";
import { AnimatePresence, motion } from "framer-motion";
import { cn } from "@/lib/utils";

export interface RelativeDrawerProps
  extends React.HTMLAttributes<HTMLDivElement> {
  isOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  side?: "left" | "right";
  width?: string | number;
  children?: React.ReactNode;
  drawer?: React.ReactNode;
  trigger?: React.ReactNode;
}

export const RelativeDrawer = React.forwardRef<
  HTMLDivElement,
  RelativeDrawerProps
>(
  (
    {
      className,
      children,
      isOpen: isOpenProp,
      onOpenChange,
      side = "right",
      width = 320,
      drawer,
      trigger,
      ...props
    },
    ref
  ) => {
    const [isOpenState, setIsOpenState] = React.useState(false);

    const isControlled = isOpenProp !== undefined;
    const isOpen = isControlled ? isOpenProp : isOpenState;

    const handleOpenChange = React.useCallback(
      (open: boolean) => {
        if (!isControlled) {
          setIsOpenState(open);
        }
        onOpenChange?.(open);
      },
      [isControlled, onOpenChange]
    );

    const handleTriggerClick = () => {
      handleOpenChange(!isOpen);
    };

    return (
      <div
        ref={ref}
        className={cn("relative flex overflow-hidden w-full h-full", className)}
        {...props}
      >
        {/* Main content that's always visible */}
        <div className="flex-1 min-w-0">
          {trigger && (
            <div onClick={handleTriggerClick} className="cursor-pointer">
              {trigger}
            </div>
          )}
          {children}
        </div>

        {/* Drawer */}
        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ width: 0, opacity: 0 }}
              animate={{
                width: typeof width === "number" ? width : width,
                opacity: 1,
              }}
              exit={{ width: 0, opacity: 0 }}
              transition={{ type: "spring", bounce: 0, duration: 0.3 }}
              className={cn(
                "overflow-hidden",
                side === "right" ? "border-l" : "border-r",
                "border-border bg-background"
              )}
              style={{
                order: side === "left" ? -1 : 1,
              }}
            >
              {drawer}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  }
);

RelativeDrawer.displayName = "RelativeDrawer";
